import {useCallback, useEffect, useState} from 'react';
import {BasicPage} from '../../../../@types/spring-data-page';
import {ExplorerDatasetType, ExtendedTreeViewItem} from '../config/explorer-mrt-types';
import {ResourceType} from '../../../../@types/resource-type';
import {ComponentState} from '../../../../@types/component';
import authService from '../../../../services/auth.service';
import {roles} from '../../../../utils/role-to-rights';

// Import existing services (they are singleton instances)
import safetyService from '../../../../services/safety.service';
import groupService from '../../../../services/group.service';
import {UserService} from '../../../../services/users.service';
import psfTemplateService from '../../../../services/psf-template.service';
import newOccurrenceService from '../../../../services/new-occurrence.service';
import serviceUserAccountService from '../../../../services/service-user-account.service';
import occurrenceReportDescriptorService from '../../../../services/occurrence-report-descriptor.service';
import irpService from '../../../../services/irp.service';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import foundryService from '../../../../services/foundry.service';
import safetyRecommendationService from '../../../../services/safety-recommendation.service';
import componentService from '../../../../services/component.service';
import {componentTreeItems} from '../../../../utils/component-utils';


// Create user service instance (it's exported as a class)
const userService = new UserService();

interface UseExplorerDataProps {
    workspaceId: string;
    dataset: ExplorerDatasetType;
    groupId?: string;
    searchText?: string;
    filters?: Record<string, any>;
    page: BasicPage;
}

interface UseExplorerDataResult {
    data: ExtendedTreeViewItem[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    totalElements?: number;
    totalPages?: number;
}

// No longer need mapping - dataset is already a ResourceType

/**
 * Recursively sorts tree items alphabetically by name.
 * Groups are sorted before non-groups at each level, then alphabetically within type.
 */
const sortTreeItemsAlphabetically = (items: ExtendedTreeViewItem[]): ExtendedTreeViewItem[] => {
    items.forEach(item => {
        if (item.children && item.children.length > 0) {
            item.children = sortTreeItemsAlphabetically(item.children);
        }
    });
    return items.sort((a, b) => {
        const aIsGroup = a.type === ResourceType.GROUPS;
        const bIsGroup = b.type === ResourceType.GROUPS;
        if (aIsGroup !== bIsGroup) {
            return aIsGroup ? -1 : 1;
        }
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
};

/**
 * Hook for fetching Explorer data from the API using existing services
 */
export function useExplorerData({
    workspaceId,
    dataset,
    groupId,
    searchText,
    filters,
    page,
}: UseExplorerDataProps): UseExplorerDataResult {
    const [data, setData] = useState<ExtendedTreeViewItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalElements, setTotalElements] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const { setWorkspaceObjects } = useWorkspaceContext();

    // Convert API response to ExtendedTreeViewItem format
    const convertToTreeViewItem = useCallback((
        resource: any,
        resourceType: ResourceType
    ): ExtendedTreeViewItem => {
        // Special handling for user names
        let displayName = resource.name || resource.title || 'Unnamed';
        if (resourceType === ResourceType.USERS) {
            // For regular users, show "lastName, firstName"
            if (resource.lastName && resource.firstName) {
                displayName = `${resource.lastName}, ${resource.firstName}`;
            } else if (resource.lastName) {
                displayName = resource.lastName;
            } else if (resource.firstName) {
                displayName = resource.firstName;
            }
        } else if (resourceType === ResourceType.SERVICE_USER_ACCOUNTS) {
            // For service users, show username
            displayName = resource.username || resource.name || 'Unnamed';
        } else if (resourceType === ResourceType.OCCURRENCE_REPORTS) {
            // For occurrence reports, use summary as the display name
            displayName = resource.summary || resource.sourceName || 'Unnamed Report';
        } else if (resourceType === ResourceType.OCCURRENCE_REPORT_DESCRIPTORS) {
            // For occurrence report descriptors, append state or version
            const baseName = resource.name || 'Unnamed Descriptor';
            if (resource.descriptorState === 'DRAFT') {
                displayName = `${baseName} (DRAFT)`;
            } else if (resource.version) {
                displayName = `${baseName} (V.${resource.version})`;
            } else {
                // eslint-disable-next-line no-console
                console.warn(`Descriptor ${baseName} has no state or version`);
                displayName = baseName;
            }
        } else if (resourceType === ResourceType.COMPONENT) {
            // A published Component shows its version; a DRAFT has none yet.
            const baseName = resource.name || 'Unnamed Component';
            displayName = resource.version
                ? `${baseName} (v${resource.version})`
                : `${baseName} (draft)`;
        }

        const base: ExtendedTreeViewItem = {
            id: resource.id?.toString() || resource.uniqueId || '',
            name: displayName,
            type: resourceType,
            object: resource,
            hasChildren: false,
        };

        // Add resource-specific properties
        switch (resourceType) {
            case ResourceType.SAFETY_MODELS:
                base.numberEventSequences = resource.numberEventSequences || 0;
                base.hasChildren = (base.numberEventSequences || 0) > 0;

                // Use pre-loaded event sequence summaries if available
                // Note: eventSequenceSummaries will only be present if includeEventSequences=true
                // AND the safety model has event sequences
                if (resource.eventSequenceSummaries) {
                    base.children = resource.eventSequenceSummaries.map((es: any) => ({
                        id: es.uniqueId || es.id?.toString() || '',
                        name: es.uniqueId ? `${es.uniqueId} - ${es.name || 'Unnamed'}` : (es.name || 'Unnamed'),
                        type: ResourceType.EVENT_SEQUENCES,
                        object: {
                            ...es,
                            containerId: resource.id  // Add container reference
                        },
                        hasChildren: false,
                    })).sort((a: ExtendedTreeViewItem, b: ExtendedTreeViewItem) =>
                        (a.id || '').localeCompare(b.id || '', undefined, { numeric: true, sensitivity: 'base' })
                    );
                } else if (resource.children && resource.children.length > 0) {
                    // Fallback to old children format if available
                    base.children = resource.children.map((child: any) =>
                        convertToTreeViewItem(child, ResourceType.EVENT_SEQUENCES)
                    );
                } else if (base.hasChildren) {
                    // Initialize empty children array if it has children but they're not loaded
                    // This happens when:
                    // 1. includeEventSequences=false (lazy loading mode)
                    // 2. includeEventSequences=true but safety model has 0 event sequences
                    base.children = [];
                }
                break;

            case ResourceType.GROUPS: {
                // Get the actual role from currentUser or role property
                // For super users, explicitly show 'SUPER' role
                let actualRole = '';
                if (authService.isSuperUser()) {
                    actualRole = roles.super; // 'SUPER'
                } else if (resource.currentUser?.role) {
                    actualRole = resource.currentUser.role;
                } else if (resource.role) {
                    actualRole = resource.role;
                }
                base.groupRole = actualRole;
                // Groups have children if the children array exists
                base.hasChildren = resource.children && resource.children.length > 0;
                break;
            }

            case ResourceType.USERS:
                // Users should already be filtered, but double-check
                if (resource.isServiceUser || resource.serviceUser) {
                    base.type = ResourceType.SERVICE_USER_ACCOUNTS;
                }
                break;

            case ResourceType.SERVICE_USER_ACCOUNTS:
                // Already the correct type
                break;
        }

        return base;
    }, []);

    // Build tree structure from flat group list
    const buildGroupTree = useCallback((flatGroups: any[]): ExtendedTreeViewItem[] => {
        const groupMap = new Map<string, ExtendedTreeViewItem>();
        const rootGroups: ExtendedTreeViewItem[] = [];
        const processedAsChild = new Set<string>();

        // First pass: create all group items from the response
        flatGroups.forEach(group => {
            const item = convertToTreeViewItem(group, ResourceType.GROUPS);
            groupMap.set(item.id, item);
        });

        // Second pass: extract missing parent groups from pathToRoot
        // This handles cases where user is member of child but not parent
        flatGroups.forEach(group => {
            if (group.pathToRoot && Array.isArray(group.pathToRoot)) {
                group.pathToRoot.forEach((ancestor: any) => {
                    const ancestorId = ancestor.id.toString();
                    if (!groupMap.has(ancestorId)) {
                        // Get the actual role from currentUser or role property
                        // For super users, explicitly show 'SUPER' role
                        let ancestorRole = '';
                        if (authService.isSuperUser()) {
                            ancestorRole = roles.super;
                        } else if (ancestor.currentUser?.role) {
                            ancestorRole = ancestor.currentUser.role;
                        } else if (ancestor.role) {
                            ancestorRole = ancestor.role;
                        }
                        // Create a placeholder for this missing parent group
                        const parentItem: ExtendedTreeViewItem = {
                            id: ancestorId,
                            name: ancestor.name || 'Unnamed Working Area',
                            type: ResourceType.GROUPS,
                            object: ancestor,
                            hasChildren: true,
                            children: [],
                            groupRole: ancestorRole
                        };
                        groupMap.set(ancestorId, parentItem);
                    }
                });
            }
        });

        // Third pass: build tree structure
        flatGroups.forEach(group => {
            const item = groupMap.get(group.id.toString());
            if (!item) return;

            if (group.parent) {
                // This is a child group
                const parentItem = groupMap.get(group.parent.toString());
                if (parentItem) {
                    if (!parentItem.children) {
                        parentItem.children = [];
                    }
                    // Check if not already added (avoid duplicates)
                    if (!parentItem.children.some(child => child.id === item.id)) {
                        parentItem.children.push(item);
                        parentItem.hasChildren = true;
                    }
                    processedAsChild.add(item.id);
                }
            }

            // Also build relationships between ancestors in pathToRoot
            // This ensures the full hierarchy is preserved (e.g., Demonstrations → PLA25 → IOC)
            if (group.pathToRoot && Array.isArray(group.pathToRoot)) {
                for (let i = group.pathToRoot.length - 1; i > 0; i--) {
                    const child = group.pathToRoot[i];
                    const parent = group.pathToRoot[i - 1];
                    const childId = child.id.toString();
                    const parentId = parent.id.toString();
                    const childItem = groupMap.get(childId);
                    const parentItem = groupMap.get(parentId);

                    if (parentItem && childItem) {
                        if (!parentItem.children) {
                            parentItem.children = [];
                        }
                        // Only add if not already present
                        if (!parentItem.children.some(c => c.id === childItem.id)) {
                            parentItem.children.push(childItem);
                            parentItem.hasChildren = true;
                        }
                        processedAsChild.add(childId);
                    }
                }
            }
        });

        // Fourth pass: collect root groups (those not added as children)
        groupMap.forEach((item, id) => {
            if (!processedAsChild.has(id)) {
                rootGroups.push(item);
            }
        });

        return sortTreeItemsAlphabetically(rootGroups);
    }, [convertToTreeViewItem]);

    // Build tree structure for Safety Models grouped by their groups
    const buildSafetyModelTree = useCallback((
        safetyModels: any[],
        components: any[] = [],
    ): ExtendedTreeViewItem[] => {
        const groupMap = new Map<string, ExtendedTreeViewItem>();
        const safetyModelMap = new Map<string, ExtendedTreeViewItem[]>();
        const componentMap = new Map<string, ExtendedTreeViewItem[]>();
        const ungroupedComponents: ExtendedTreeViewItem[] = [];
        const processedGroups = new Set<string>();

        // Safety models and components are both owned by a working area and both belong in this
        // dataset: a component is part of a safety model, not a separate kind of thing. The group
        // passes below walk them together so a working area holding only components still appears.
        // Published Components stay in this tree too -- it is membership-gated and shows every
        // version, draft and released, alongside the safety models the working area owns; the
        // component catalogue is the separate, flat, everyone-can-see view onto the same objects
        // (component-solution-spec.md section 5, "the working area tree is unchanged").
        const grouped: any[] = [...safetyModels, ...components];

        // First, collect all unique groups and organize Safety Models by group
        safetyModels.forEach(sm => {
            const smItem = convertToTreeViewItem(sm, ResourceType.SAFETY_MODELS);
            const groupId = sm.group?.id?.toString();

            if (groupId) {
                if (!safetyModelMap.has(groupId)) {
                    safetyModelMap.set(groupId, []);
                }
                safetyModelMap.get(groupId)!.push(smItem);

                // Create group item if not exists
                if (!groupMap.has(groupId)) {
                    // Get the actual role from currentUser or role property
                    // For super users, explicitly show 'SUPER' role
                    let groupRole = '';
                    if (authService.isSuperUser()) {
                        groupRole = roles.super; // 'SUPER'
                    } else if (sm.group?.currentUser?.role) {
                        groupRole = sm.group.currentUser.role;
                    } else if (sm.group?.role) {
                        groupRole = sm.group.role;
                    }
                    const groupItem: ExtendedTreeViewItem = {
                        id: `group-${groupId}`, // Prefix to avoid ID conflicts
                        name: sm.group.name || 'Unnamed Working Area',
                        type: ResourceType.GROUPS,
                        object: sm.group,
                        hasChildren: true,
                        children: [],
                        groupRole: groupRole
                    };
                    groupMap.set(groupId, groupItem);
                }
            } else {
                // Handle Safety Models without groups
                const ungroupedItem: ExtendedTreeViewItem = {
                    ...smItem,
                    hasChildren: smItem.hasChildren || false
                };
                groupMap.set(`sm-${sm.id}`, ungroupedItem);
            }
        });

        // Organise components by working area, creating any group row the safety models did not.
        components.forEach(component => {
            const item = convertToTreeViewItem(component, ResourceType.COMPONENT);
            const componentGroupId = component.group?.id?.toString();

            if (!componentGroupId) {
                // A component that belongs to no working area is not expected (the backend
                // requires one), but it is listed after the working areas rather than dropped.
                ungroupedComponents.push(item);
                return;
            }

            if (!componentMap.has(componentGroupId)) {
                componentMap.set(componentGroupId, []);
            }
            componentMap.get(componentGroupId)!.push(item);

            if (!groupMap.has(componentGroupId)) {
                let groupRole = '';
                if (authService.isSuperUser()) {
                    groupRole = roles.super;
                } else if (component.group?.currentUser?.role) {
                    groupRole = component.group.currentUser.role;
                } else if (component.group?.role) {
                    groupRole = component.group.role;
                }
                groupMap.set(componentGroupId, {
                    id: `group-${componentGroupId}`,
                    name: component.group.name || 'Unnamed Working Area',
                    type: ResourceType.GROUPS,
                    object: component.group,
                    hasChildren: true,
                    children: [],
                    groupRole: groupRole,
                });
            }
        });

        // Build parent-child relationships between groups
        // First, ensure ALL ancestors from pathToRoot exist in the groupMap
        grouped.forEach(sm => {
            if (sm.group?.pathToRoot && Array.isArray(sm.group.pathToRoot)) {
                sm.group.pathToRoot.forEach((ancestor: any) => {
                    const ancestorId = ancestor.id.toString();
                    if (!groupMap.has(ancestorId)) {
                        // Get the actual role from currentUser or role property
                        let ancestorRole = '';
                        if (authService.isSuperUser()) {
                            ancestorRole = roles.super;
                        } else if (ancestor.currentUser?.role) {
                            ancestorRole = ancestor.currentUser.role;
                        } else if (ancestor.role) {
                            ancestorRole = ancestor.role;
                        }
                        const ancestorItem: ExtendedTreeViewItem = {
                            id: `group-${ancestorId}`,
                            name: ancestor.name || 'Unnamed Working Area',
                            type: ResourceType.GROUPS,
                            object: ancestor,
                            hasChildren: true,
                            children: [],
                            groupRole: ancestorRole
                        };
                        groupMap.set(ancestorId, ancestorItem);
                    }
                });
            }
        });

        // Now build the parent-child relationships
        grouped.forEach(sm => {
            if (sm.group?.parent) {
                const parentId = sm.group.parent.toString();
                const childId = sm.group.id.toString();
                const childItem = groupMap.get(childId);
                const parentItem = groupMap.get(parentId);

                if (parentItem && childItem) {
                    if (!parentItem.children) {
                        parentItem.children = [];
                    }
                    // Only add if not already present
                    if (!parentItem.children.some(c => c.id === childItem.id)) {
                        parentItem.children.push(childItem);
                        processedGroups.add(childId);
                    }
                }
            }

            // Also build relationships between ancestors in pathToRoot
            if (sm.group?.pathToRoot && Array.isArray(sm.group.pathToRoot)) {
                for (let i = sm.group.pathToRoot.length - 1; i > 0; i--) {
                    const child = sm.group.pathToRoot[i];
                    const parent = sm.group.pathToRoot[i - 1];
                    const childId = child.id.toString();
                    const parentId = parent.id.toString();
                    const childItem = groupMap.get(childId);
                    const parentItem = groupMap.get(parentId);

                    if (parentItem && childItem) {
                        if (!parentItem.children) {
                            parentItem.children = [];
                        }
                        // Only add if not already present
                        if (!parentItem.children.some(c => c.id === childItem.id)) {
                            parentItem.children.push(childItem);
                            processedGroups.add(childId);
                        }
                    }
                }
            }
        });

        // Assign Safety Models to their groups
        safetyModelMap.forEach((sms, groupId) => {
            const groupItem = groupMap.get(groupId);
            if (groupItem) {
                if (!groupItem.children) {
                    groupItem.children = [];
                }
                groupItem.children.push(...sms);
                groupItem.hasChildren = true;
            }
        });

        // Assign Components to the same working areas, alongside the safety models
        componentMap.forEach((componentItems, componentGroupId) => {
            const groupItem = groupMap.get(componentGroupId);
            if (groupItem) {
                if (!groupItem.children) {
                    groupItem.children = [];
                }
                groupItem.children.push(...componentItems);
                groupItem.hasChildren = true;
            }
        });

        // Collect all root items (groups without parents and ungrouped Safety Models)
        const rootItems: ExtendedTreeViewItem[] = [];
        groupMap.forEach((item, key) => {
            // Check if it's a group - map keys are numeric group IDs, not prefixed
            if (key.match(/^\d+$/)) {
                // It's a numeric group ID - only add if not processed as a child
                if (!processedGroups.has(key)) {
                    rootItems.push(item);
                }
            } else if (key.startsWith('sm-')) {
                // It's an ungrouped Safety Model
                rootItems.push(item);
            }
        });

        ungroupedComponents.sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}));
        return [...sortTreeItemsAlphabetically(rootItems), ...ungroupedComponents];
    }, [convertToTreeViewItem]);

    // Build tree structure for PSF Templates grouped by their groups
    const buildPsfTemplateTree = useCallback((psfTemplates: any[]): ExtendedTreeViewItem[] => {
        const groupMap = new Map<string, ExtendedTreeViewItem>();
        const psfMap = new Map<string, ExtendedTreeViewItem[]>();
        const processedGroups = new Set<string>();

        // First, collect all unique groups and organize PSFs by group
        psfTemplates.forEach(psf => {
            const psfItem = convertToTreeViewItem(psf, ResourceType.PERFORMANCE_SHAPING_FACTORS);
            const groupId = psf.group?.id?.toString();

            if (groupId) {
                if (!psfMap.has(groupId)) {
                    psfMap.set(groupId, []);
                }
                psfMap.get(groupId)!.push(psfItem);

                // Create group item if not exists
                if (!groupMap.has(groupId)) {
                    // Get the actual role from currentUser or role property
                    // For super users, explicitly show 'SUPER' role
                    let groupRole = '';
                    if (authService.isSuperUser()) {
                        groupRole = roles.super; // 'SUPER'
                    } else if (psf.group?.currentUser?.role) {
                        groupRole = psf.group.currentUser.role;
                    } else if (psf.group?.role) {
                        groupRole = psf.group.role;
                    }
                    const groupItem: ExtendedTreeViewItem = {
                        id: `group-${groupId}`, // Prefix to avoid ID conflicts
                        name: psf.group.name || 'Unnamed Working Area',
                        type: ResourceType.GROUPS,
                        object: psf.group,
                        hasChildren: true,
                        children: [],
                        groupRole: groupRole
                    };
                    groupMap.set(groupId, groupItem);
                }
            } else {
                // Handle PSF templates without groups
                const ungroupedItem: ExtendedTreeViewItem = {
                    ...psfItem,
                    hasChildren: false
                };
                groupMap.set(`psf-${psf.id}`, ungroupedItem);
            }
        });

        // Build parent-child relationships between groups
        // First, ensure ALL ancestors from pathToRoot exist in the groupMap
        psfTemplates.forEach(psf => {
            if (psf.group?.pathToRoot && Array.isArray(psf.group.pathToRoot)) {
                psf.group.pathToRoot.forEach((ancestor: any) => {
                    const ancestorId = ancestor.id.toString();
                    if (!groupMap.has(ancestorId)) {
                        // Get the actual role from currentUser or role property
                        let ancestorRole = '';
                        if (authService.isSuperUser()) {
                            ancestorRole = roles.super;
                        } else if (ancestor.currentUser?.role) {
                            ancestorRole = ancestor.currentUser.role;
                        } else if (ancestor.role) {
                            ancestorRole = ancestor.role;
                        }
                        const ancestorItem: ExtendedTreeViewItem = {
                            id: `group-${ancestorId}`,
                            name: ancestor.name || 'Unnamed Working Area',
                            type: ResourceType.GROUPS,
                            object: ancestor,
                            hasChildren: true,
                            children: [],
                            groupRole: ancestorRole
                        };
                        groupMap.set(ancestorId, ancestorItem);
                    }
                });
            }
        });

        // Now build the parent-child relationships
        psfTemplates.forEach(psf => {
            if (psf.group?.parent) {
                const parentId = psf.group.parent.toString();
                const childId = psf.group.id.toString();
                const childItem = groupMap.get(childId);
                const parentItem = groupMap.get(parentId);

                if (parentItem && childItem) {
                    if (!parentItem.children) {
                        parentItem.children = [];
                    }
                    // Only add if not already present
                    if (!parentItem.children.some(c => c.id === childItem.id)) {
                        parentItem.children.push(childItem);
                        processedGroups.add(childId);
                    }
                }
            }

            // Also build relationships between ancestors in pathToRoot
            if (psf.group?.pathToRoot && Array.isArray(psf.group.pathToRoot)) {
                for (let i = psf.group.pathToRoot.length - 1; i > 0; i--) {
                    const child = psf.group.pathToRoot[i];
                    const parent = psf.group.pathToRoot[i - 1];
                    const childId = child.id.toString();
                    const parentId = parent.id.toString();
                    const childItem = groupMap.get(childId);
                    const parentItem = groupMap.get(parentId);

                    if (parentItem && childItem) {
                        if (!parentItem.children) {
                            parentItem.children = [];
                        }
                        // Only add if not already present
                        if (!parentItem.children.some(c => c.id === childItem.id)) {
                            parentItem.children.push(childItem);
                            processedGroups.add(childId);
                        }
                    }
                }
            }
        });

        // Assign PSF templates to their groups
        psfMap.forEach((psfs, groupId) => {
            const groupItem = groupMap.get(groupId);
            if (groupItem) {
                if (!groupItem.children) {
                    groupItem.children = [];
                }
                groupItem.children.push(...psfs);
                groupItem.hasChildren = true;
            }
        });

        // Collect all root items (groups without parents and ungrouped PSFs)
        const rootItems: ExtendedTreeViewItem[] = [];
        groupMap.forEach((item, key) => {
            // Check if it's a group - map keys are numeric group IDs, not prefixed
            if (key.match(/^\d+$/)) {
                // It's a numeric group ID - only add if not processed as a child
                if (!processedGroups.has(key)) {
                    rootItems.push(item);
                }
            } else if (key.startsWith('psf-')) {
                // It's an ungrouped PSF
                rootItems.push(item);
            }
        });

        return sortTreeItemsAlphabetically(rootItems);
    }, [convertToTreeViewItem]);

    // Build tree structure for Integrated Risk Pictures grouped by their groups
    const buildIRPTree = useCallback((irps: any[]): ExtendedTreeViewItem[] => {
        const groupMap = new Map<string, ExtendedTreeViewItem>();
        const irpMap = new Map<string, ExtendedTreeViewItem[]>();
        const processedGroups = new Set<string>();

        // First pass: collect all unique groups and organise IRPs by group
        irps.forEach(irp => {
            const irpItem = convertToTreeViewItem(irp, ResourceType.INTEGRATED_RISK_PICTURES);
            const grpId = irp.group?.id?.toString();

            if (grpId) {
                if (!irpMap.has(grpId)) {
                    irpMap.set(grpId, []);
                }
                irpMap.get(grpId)!.push(irpItem);

                if (!groupMap.has(grpId)) {
                    let groupRole = '';
                    if (authService.isSuperUser()) {
                        groupRole = roles.super;
                    } else if (irp.group?.currentUser?.role) {
                        groupRole = irp.group.currentUser.role;
                    } else if (irp.group?.role) {
                        groupRole = irp.group.role;
                    }
                    groupMap.set(grpId, {
                        id: `group-${grpId}`,
                        name: irp.group.name || 'Unnamed Group',
                        type: ResourceType.GROUPS,
                        object: irp.group,
                        hasChildren: true,
                        children: [],
                        groupRole,
                    });
                }
            } else {
                groupMap.set(`irp-${irp.id}`, { ...irpItem, hasChildren: false });
            }
        });

        // Second pass: ensure all ancestors from pathToRoot exist in the groupMap
        irps.forEach(irp => {
            if (irp.group?.pathToRoot && Array.isArray(irp.group.pathToRoot)) {
                irp.group.pathToRoot.forEach((ancestor: any) => {
                    const ancestorId = ancestor.id.toString();
                    if (!groupMap.has(ancestorId)) {
                        let ancestorRole = '';
                        if (authService.isSuperUser()) {
                            ancestorRole = roles.super;
                        } else if (ancestor.currentUser?.role) {
                            ancestorRole = ancestor.currentUser.role;
                        } else if (ancestor.role) {
                            ancestorRole = ancestor.role;
                        }
                        groupMap.set(ancestorId, {
                            id: `group-${ancestorId}`,
                            name: ancestor.name || 'Unnamed Group',
                            type: ResourceType.GROUPS,
                            object: ancestor,
                            hasChildren: true,
                            children: [],
                            groupRole: ancestorRole,
                        });
                    }
                });
            }
        });

        // Third pass: build parent-child relationships between groups
        irps.forEach(irp => {
            if (irp.group?.parent) {
                const parentId = irp.group.parent.toString();
                const childId = irp.group.id.toString();
                const childItem = groupMap.get(childId);
                const parentItem = groupMap.get(parentId);
                if (parentItem && childItem && !parentItem.children!.some(c => c.id === childItem.id)) {
                    parentItem.children!.push(childItem);
                    processedGroups.add(childId);
                }
            }

            if (irp.group?.pathToRoot && Array.isArray(irp.group.pathToRoot)) {
                for (let i = irp.group.pathToRoot.length - 1; i > 0; i--) {
                    const child = irp.group.pathToRoot[i];
                    const parent = irp.group.pathToRoot[i - 1];
                    const childId = child.id.toString();
                    const parentId = parent.id.toString();
                    const childItem = groupMap.get(childId);
                    const parentItem = groupMap.get(parentId);
                    if (parentItem && childItem && !parentItem.children!.some(c => c.id === childItem.id)) {
                        parentItem.children!.push(childItem);
                        processedGroups.add(childId);
                    }
                }
            }
        });

        // Fourth pass: assign IRPs to their groups
        irpMap.forEach((irpItems, grpId) => {
            const groupItem = groupMap.get(grpId);
            if (groupItem) {
                if (!groupItem.children) {
                    groupItem.children = [];
                }
                groupItem.children.push(...irpItems);
            }
        });

        // Collect root items (groups without parents and ungrouped IRPs)
        const rootItems: ExtendedTreeViewItem[] = [];
        groupMap.forEach((item, key) => {
            if (key.match(/^\d+$/)) {
                if (!processedGroups.has(key)) {
                    rootItems.push(item);
                }
            } else if (key.startsWith('irp-')) {
                rootItems.push(item);
            }
        });

        return sortTreeItemsAlphabetically(rootItems);
    }, [convertToTreeViewItem]);

    // Fetch data from API using services
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let response: any;
            // Components fetched alongside the safety models, for the shared working-area tree.
            let safetyModelComponents: any[] = [];

            switch (dataset) {
                case ResourceType.SAFETY_MODELS: {
                    // Use the safety service - load all models at once (max few hundred)
                    // to avoid pagination issues with tree structure
                    const allModelsPage = { ...page, size: 10000, offset: 0 };
                    // Components share this dataset, so both are fetched together and folded into
                    // one working-area tree. Requested in parallel because neither depends on the
                    // other and the tree cannot be built until both have arrived.
                    const [modelsResponse, componentsResponse] = await Promise.all([
                        safetyService.fetchPagedSafetyModels(
                            allModelsPage,
                            groupId || null,
                            searchText,
                            true  // Include event sequences for explorer
                        ),
                        componentService.fetchPagedComponents(
                            { ...page, size: 10000, offset: 0, sort: 'name' },
                            {
                                searchTerm: searchText,
                                groupId: groupId ? parseInt(groupId) : undefined,
                                includeSubGroups: true,
                            },
                        ),
                    ]);
                    response = modelsResponse;
                    safetyModelComponents = componentsResponse?.content ?? [];
                    break;
                }

                case ResourceType.GROUPS: {
                    // Use the group service - load all groups at once
                    // to avoid pagination issues with tree structure
                    const allGroupsPage = { ...page, size: 10000, offset: 0 };
                    response = await groupService.fetchPagedGroups(allGroupsPage, null, searchText);
                    break;
                }

                case ResourceType.USERS: {
                    // Use the user service - users only, sorted by created (latest first)
                    const usersPage = { ...page, sort: 'created', direction: 'DESC' };
                    response = await userService.getPagedUsers(usersPage, null, searchText);
                    // Note: The user service returns only regular users, not service users
                    break;
                }

                case ResourceType.SERVICE_USER_ACCOUNTS: {
                    // Use the service user account service - sorted by username
                    const serviceUsersPage = { ...page, sort: 'username' };
                    response = await serviceUserAccountService.getPagedServiceUsers(serviceUsersPage, null, searchText);
                    break;
                }

                case ResourceType.NEW_OCCURRENCE_REPORTS: {
                    // Use the new occurrence service (reports API) - reports are sorted by dateTime
                    const reportsPage = { ...page, sort: 'datetime' };
                    const supersededParam = filters?.showSuperseded ? undefined : false;
                    response = await newOccurrenceService.fetchOccurrences(
                        reportsPage,
                        groupId ? parseInt(groupId) : null,
                        searchText,
                        undefined,  // startSearchDate
                        undefined,  // endSearchDate
                        filters?.sourceNames,
                        filters?.descriptorTermIds,
                        supersededParam
                    );
                    break;
                }

                case ResourceType.FOUNDRY_EVENTS: {
                    const foundryPage = { ...page, sort: 'eventDate', direction: 'desc' };
                    response = await foundryService.fetchEvents(
                        foundryPage,
                        searchText,
                        filters?.sourceNames,
                    );
                    break;
                }

                case ResourceType.COMPONENT_CATALOGUE: {
                    // The catalogue: published Components, flat -- no working-area nesting
                    // (component-solution-spec.md section 5). A derived view, not a new object,
                    // so this reuses the same paged fetch the working-area tree uses, restricted
                    // to PUBLISHED. groupId, when the Working Area filter picks one, narrows to
                    // that exact working area only -- not its subgroups, matching how the source
                    // filter on Occurrence Reports is a plain exact match too.
                    response = await componentService.fetchPagedComponents(
                        { ...page, sort: 'name' },
                        {
                            searchTerm: searchText,
                            states: [ComponentState.PUBLISHED],
                            groupId: filters?.groupId,
                            mappedAgainstSource: filters?.mappedAgainstSource,
                            hasUnmappedSources: filters?.hasUnmappedSources,
                        },
                    );
                    break;
                }

                case ResourceType.SAFETY_RECOMMENDATIONS: {
                    // The dataset's rows are the sources; the recommendations themselves are
                    // searched in the register tab a row opens.
                    const sources = await safetyRecommendationService.getSources();
                    response = {
                        content: sources.map((source) => ({
                            id: source.code,
                            name: source.name,
                            recommendationCount: source.recommendationCount,
                            lastSyncedAt: source.lastSyncedAt,
                            jobRunning: source.jobRunning,
                        })),
                        totalElements: sources.length,
                        totalPages: 1,
                    };
                    break;
                }

                case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS: {
                    // Use the occurrence report descriptor service - sorted by name
                    const descriptorsPage = { ...page, sort: 'name' };
                    response = await occurrenceReportDescriptorService.fetchDescriptors(descriptorsPage, groupId ? parseInt(groupId) : null, searchText);
                    break;
                }

                case ResourceType.PERFORMANCE_SHAPING_FACTORS:
                    // Use the PSF template service
                    response = await psfTemplateService.fetchPsfTemplates(page, groupId ? parseInt(groupId) : null, searchText);
                    break;

                case ResourceType.INTEGRATED_RISK_PICTURES: {
                    // Load all IRPs at once so they can be organised into a group tree
                    const pageResult = await irpService.fetchPagedIntegratedRiskPictures(
                        0,
                        10000,
                        'name,asc',
                        {
                            status: filters?.status,
                            archived: filters?.archived
                        }
                    );

                    response = pageResult;
                    break;
                }

                case ResourceType.WORKSHOPS:
                case ResourceType.RISK_FORECASTS:
                case ResourceType.OPERATIONAL_CHANGES:
                case ResourceType.TRAFFIC_MODELS:
                    // These don't have services yet, return empty
                    if (process.env.NODE_ENV === 'development') {
                        // eslint-disable-next-line no-console
                        console.warn(`No service available for dataset: ${dataset}`);
                    }
                    response = {
                        content: [],
                        page: {
                            totalElements: 0,
                            totalPages: 0,
                            number: 0,
                            size: page.size || 20
                        }
                    };
                    break;

                default:
                    throw new Error(`Unknown dataset: ${dataset}`);
            }

            // Convert response to tree view items
            if (response && response.content) {
                let items: ExtendedTreeViewItem[];

                if (dataset === ResourceType.GROUPS) {
                    // Groups need special handling to build tree structure
                    items = buildGroupTree(response.content);
                } else if (dataset === ResourceType.SAFETY_MODELS) {
                    // Safety Models and Components share one working-area tree
                    items = buildSafetyModelTree(response.content, safetyModelComponents);
                } else if (dataset === ResourceType.PERFORMANCE_SHAPING_FACTORS) {
                    // PSF Templates also need to be organized by groups
                    items = buildPsfTemplateTree(response.content);
                } else if (dataset === ResourceType.INTEGRATED_RISK_PICTURES) {
                    // IRPs are organised by group hierarchy
                    items = buildIRPTree(response.content);
                } else if (dataset === ResourceType.SERVICE_USER_ACCOUNTS) {
                    // Service users need to be explicitly typed
                    items = response.content.map((item: any) => {
                        const baseItem = convertToTreeViewItem(item, ResourceType.SERVICE_USER_ACCOUNTS);
                        return baseItem;
                    });
                } else if (dataset === ResourceType.COMPONENT_CATALOGUE) {
                    // Rows are typed COMPONENT, not COMPONENT_CATALOGUE (the dataset they were
                    // fetched under) -- the same convention the working-area tree already uses,
                    // so a click routes through the existing COMPONENT -> COMPONENT_TREE rewrite
                    // and opens the read-only canvas rather than needing a new editor.
                    items = componentTreeItems(response.content);
                } else {
                    // Other resources are flat lists
                    items = response.content.map((item: any) =>
                        convertToTreeViewItem(item, dataset)
                    );
                }

                setData(items);

                // You have to set workspaceObjects type as the New Explorer CREATE button depends on it.
                // Without it, the CREATE button will not show up during the initial load (see issue 1447).
                // This fixes the issue 1447 with the CREATE button not being displayed.
                setWorkspaceObjects({objectType: dataset});

                // Handle both HATEOAS format (with nested page) and direct format
                if (response.totalElements !== undefined) {
                    // Direct format (IRP)
                    setTotalElements(response.totalElements || 0);
                    setTotalPages(response.totalPages || 0);
                } else if (response.page) {
                    // HATEOAS format (most services)
                    setTotalElements(response.page.totalElements || 0);
                    setTotalPages(response.page.totalPages || 0);
                }
            } else {
                setData([]);
                setTotalElements(0);
                setTotalPages(0);
            }
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.error('Error fetching explorer data:', err);
            }
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load data. Please try again.'
            );
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [workspaceId, dataset, groupId, searchText, filters, page, convertToTreeViewItem, buildGroupTree, buildSafetyModelTree, buildPsfTemplateTree, buildIRPTree]);

    // Fetch data when dependencies change
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch: fetchData,
        totalElements,
        totalPages,
    };
}
